import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { LaneIcon, type LaneKind } from '../components/LaneIcon';
import { useApp, type NavScope, type PageId } from '../store/AppContext';
import { NAV_GROUPS } from './navGroups';

type SearchItem = {
  key: string;
  label: string;
  lane?: LaneKind;
  hint: string;
  kind: string;
  words: string;
  run: () => void;
};

type SettingTarget = { id: string; label: string; section: string; words: string };

const SETTING_TARGETS: SettingTarget[] = [
  { id: 'pwOld', label: 'Сменить пароль', section: 'Пароль', words: 'пароль password вход' },
  { id: 'stConcurrency', label: 'Профилей параллельно на ноду', section: 'Фарм / деплой', words: 'потоки параллельно concurrency браузеры' },
  { id: 'stRamPerBrowser', label: 'ОЗУ на браузер', section: 'Фарм / деплой', words: 'озу ram память мб' },
  { id: 'stContinuousFarm', label: 'Бесконечный фарм', section: 'Фарм / деплой', words: 'фарм прогрев farm' },
  { id: 'tgToken', label: 'Токен Telegram-бота', section: 'Telegram', words: 'telegram телеграм бот токен' },
  { id: 'tg', label: 'Telegram chat ID', section: 'Telegram', words: 'telegram телеграм чат канал chat' },
  { id: 'tgUsers', label: 'Кому бот отвечает на команды', section: 'Telegram', words: 'telegram телеграм бот доступ пользователи' },
  { id: 'notifyCritical', label: 'Уведомления о критических ошибках', section: 'Telegram', words: 'уведомления ошибки критические alert' },
  { id: 'resetStatsBtn', label: 'Сбросить статистику', section: 'Опасная зона', words: 'сброс статистика обнулить reset' },
];

const SCOPE_LABEL: Record<NavScope, string> = { bing: 'Bing', google: 'Google' };
const SEARCH_SCOPES: NavScope[] = ['bing'];
const MAX_RESULTS = 8;

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');

function focusWhenReady(id: string) {
  const started = performance.now();
  const tick = () => {
    const el = document.getElementById(id);
    if (!el) {
      if (performance.now() - started < 1500) requestAnimationFrame(tick);
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
    const row = el.closest('.field-row, .set-row') ?? el;
    row.classList.remove('search-flash');
    void (row as HTMLElement).offsetWidth;
    row.classList.add('search-flash');
    window.setTimeout(() => row.classList.remove('search-flash'), 1600);
  };
  requestAnimationFrame(tick);
}

export function HeaderSearch() {
  const { goto, ui, isSuperadmin, theme, toggleTheme, logout } = useApp();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const settingsScope: NavScope = ui.scope === 'google' ? 'bing' : ui.scope;

  const items = useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = [];
    for (const scope of SEARCH_SCOPES) {
      for (const group of NAV_GROUPS) {
        for (const item of group.items) {
          if (item.page === 'users' && !isSuperadmin) continue;
          out.push({
            key: `page:${scope}:${item.page}`,
            label: item.label,
            lane: item.lane,
            hint: `${SCOPE_LABEL[scope]} · /${scope}/${item.page}`,
            kind: 'Вкладка',
            words: `${item.label} ${group.title} ${SCOPE_LABEL[scope]} ${item.page}`,
            run: () => goto(item.page, scope),
          });
        }
      }
      const settings: PageId = 'settings';
      out.push({
        key: `page:${scope}:${settings}`,
        label: 'Общие настройки',
        hint: `${SCOPE_LABEL[scope]} · /${scope}/${settings}`,
        kind: 'Вкладка',
        words: `общие настройки settings ${SCOPE_LABEL[scope]}`,
        run: () => goto(settings, scope),
      });
    }
    for (const t of SETTING_TARGETS) {
      out.push({
        key: `setting:${t.id}`,
        label: t.label,
        hint: `Настройки · ${t.section}`,
        kind: 'Настройка',
        words: `${t.label} ${t.section} настройки ${t.words}`,
        run: () => {
          goto('settings', settingsScope);
          focusWhenReady(t.id);
        },
      });
    }
    out.push({
      key: 'action:theme',
      label: theme === 'dark' ? 'Светлая тема' : 'Тёмная тема',
      hint: 'Оформление',
      kind: 'Действие',
      words: 'тема светлая темная dark light оформление',
      run: toggleTheme,
    });
    out.push({
      key: 'action:logout',
      label: 'Выйти из панели',
      hint: 'Аккаунт',
      kind: 'Действие',
      words: 'выход выйти logout аккаунт',
      run: () => void logout(),
    });
    return out;
  }, [goto, isSuperadmin, logout, settingsScope, theme, toggleTheme]);

  const results = useMemo(() => {
    const terms = norm(query).trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    const q = terms.join(' ');
    return items
      .filter((it) => {
        const hay = norm(`${it.label} ${it.hint} ${it.words}`);
        return terms.every((t) => hay.includes(t));
      })
      .sort((a, b) => Number(norm(b.label).startsWith(q)) - Number(norm(a.label).startsWith(q)))
      .slice(0, MAX_RESULTS);
  }, [items, query]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    const onKey = (ev: globalThis.KeyboardEvent) => {
      if ((ev.ctrlKey || ev.metaKey) && ev.code === 'KeyK') {
        ev.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const choose = (item: SearchItem) => {
    item.run();
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown' && results.length) {
      ev.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (ev.key === 'ArrowUp' && results.length) {
      ev.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (ev.key === 'Enter' && results[active]) {
      ev.preventDefault();
      choose(results[active]);
    } else if (ev.key === 'Escape') {
      setQuery('');
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showList = open && query.trim().length > 0;

  return (
    <div className="ab-search">
      <svg className="ab-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && results[active] ? `${listId}-${active}` : undefined}
        aria-label="Поиск по панели"
        placeholder="Поиск по разделам и настройкам"
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <kbd className="ab-search-kbd">Ctrl K</kbd>
      {showList ? (
        <ul className="ab-search-list" id={listId} role="listbox">
          {results.length ? (
            results.map((it, i) => (
              <li
                key={it.key}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? 'active' : undefined}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(it)}
              >
                <span className="ab-search-kind">{it.kind}</span>
                <span className="ab-search-label">
                  {it.lane ? <LaneIcon kind={it.lane} /> : null}
                  {it.label}
                </span>
                <span className="ab-search-hint">{it.hint}</span>
              </li>
            ))
          ) : (
            <li className="ab-search-empty" role="option" aria-selected={false} aria-disabled="true">
              Ничего не найдено
            </li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
